import SeoHead from "@/components/SeoHead";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Accueil",
      "item": "https://carreaux-bat.ch/"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Contact",
      "item": "https://carreaux-bat.ch/contact"
    }
  ]
};

const contactPageSchema = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "name": "Contact Carreaux-Bat Valais",
  "description": "Contactez Carreaux-Bat pour un devis de carrelage ou rénovation de salle de bain en Valais.",
  "url": "https://carreaux-bat.ch/contact",
  "mainEntity": {
    "@type": "LocalBusiness",
    "name": "Carreaux-Bat Valais",
    "telephone": "+41 77 511 75 52",
    "email": "info@carreaux-bat.ch",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Rue de Pramagnon 32",
      "addressLocality": "Grône",
      "addressRegion": "Valais",
      "postalCode": "3979",
      "addressCountry": "CH"
    }
  }
};

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const form = e.currentTarget;
    const data = new FormData(form);
    const jsonBody: Record<string, string> = {};
    data.forEach((value, key) => {
      jsonBody[key] = value.toString();
    });
    try {
      const res = await fetch("https://formspree.io/f/xeenloye", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(jsonBody),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const errData = await res.json().catch(() => null);
        console.error("Formspree error:", errData);
        setError("Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError("Une erreur est survenue. Veuillez réessayer ou nous contacter par téléphone.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Contact Carreleur à Proximité | Carreaux-Bat Valais — Devis Gratuit"
        description="Vous cherchez un carreleur à proximité de chez vous ? Contactez Carreaux-Bat, entreprise de carrelage et société de carrelage à Grône, Valais. Devis gratuit pour service de carrelage près de chez vous à Sion, Sierre, Martigny, Crans-Montana, Verbier, Nendaz, Vercorin et Nax. Pose de carrelage, rénovation salle de bain."
        keywords="contact carreleur à proximité, devis carrelage à proximité Valais, carreleur près de chez moi, entreprise de carrelage près de chez vous Grône, société de carrelage dans votre région, service de carrelage à proximité, devis carreleur Sion, devis carreleur Sierre, devis carreleur Martigny, devis carreleur Crans-Montana, devis carreleur Verbier, devis carreleur Nendaz, devis carreleur Vercorin, devis carreleur Nax, carrelage devis gratuit Valais"
        canonical="https://carreaux-bat.ch/contact"
        ogTitle="Contactez Carreaux-Bat Valais — Carreleur à Proximité, Devis Gratuit"
        ogDescription="Vous cherchez un carreleur à proximité ? Contactez notre équipe à Grône pour un devis de carrelage ou rénovation de salle de bain près de chez vous dans le Valais."
        ogImage="/images/contact-hero.jpg"
        geoPosition="46.2304;7.4381"
        geoPlacename="Grône, Valais, Suisse"
        geoRegion="CH-VS"
        schema={{
          "@context": "https://schema.org",
          "@graph": [contactPageSchema, breadcrumbSchema]
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-52 pb-40 overflow-hidden">
        <img
          src="/images/contact-hero.jpg"
          alt="Contact carrelage Valais"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block mb-4 px-4 py-1.5 bg-amber-700/80 text-white text-xs font-medium tracking-widest uppercase rounded-full">
            Contactez-nous
          </span>
          <h1 className="text-5xl font-bold text-white mb-4">Envoyez-nous un message</h1>
          <p className="text-white/70 max-w-xl mx-auto text-sm leading-relaxed">
            On répond en général le jour même ou le lendemain. Pas de robot, pas de formulaire qui part dans le vide — c'est directement nous qui lisons.
          </p>
          <div className="flex items-center justify-center gap-2 mt-6 text-sm text-white/50">
            <Link to="/" className="hover:text-amber-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">Contact</span>
          </div>
        </div>
      </section>

      {/* Contact content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Info */}
            <div>
              <span className="text-xs uppercase tracking-widest text-amber-700 font-medium">Nos coordonnées</span>
              <h2 className="mt-2 text-3xl font-bold text-stone-900 mb-6">Parlons de votre projet</h2>
              <p className="text-sm text-stone-500 leading-relaxed mb-8">
                Vous avez une salle de bain à refaire, du carrelage à poser, ou juste une question technique ? Écrivez-nous. On préfère discuter de vive voix ou par message plutôt que de promettre des délais qu'on ne peut pas tenir.
              </p>

              <div className="space-y-5 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-2-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">Adresse</p>
                    <p className="text-sm text-stone-500 mt-0.5">Rue de Pramagnon 32<br />3979 Grône, Valais, Suisse</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">Téléphone</p>
                    <a href="tel:+41775117552" className="text-sm text-amber-700 hover:text-amber-800 mt-0.5 block">+41 77 511 75 52</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">Email</p>
                    <a href="mailto:info@carreaux-bat.ch" className="text-sm text-amber-700 hover:text-amber-800 mt-0.5 block">info@carreaux-bat.ch</a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-amber-700 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-stone-900">Horaires</p>
                    <p className="text-sm text-stone-500 mt-0.5">Lun–Ven : 7h00–18h00<br />Sam : 8h00–12h00</p>
                  </div>
                </div>
              </div>

              {/* Map embed */}
              <div className="rounded-lg overflow-hidden h-56">
                <iframe
                  title="Carreaux-Bat Grône"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2748.0!2d7.4!3d46.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478ee6b0b0b0b0b0%3A0x0!2sRue+de+Pramagnon+32%2C+3979+Gr%C3%B4ne!5e0!3m2!1sfr!2sch!4v1700000000000!5m2!1sfr!2sch"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div>
              {submitted ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-4">
                    <i className="ri-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-stone-900 mb-2">Message bien reçu !</h3>
                  <p className="text-sm text-stone-500 max-w-xs">
                    Merci. On vous recontacte rapidement, en général dans la journée ou le lendemain.
                  </p>
                </div>
              ) : (
                <form
                  id="devis-form"
                  onSubmit={handleSubmit}
                  className="bg-stone-50 rounded-lg p-8 border border-stone-100"
                >
                  <h3 className="text-xl font-bold text-stone-900 mb-6">Parlez-nous de votre projet</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1.5">Prénom *</label>
                      <input
                        type="text"
                        name="prenom"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="Jean"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1.5">Nom *</label>
                      <input
                        type="text"
                        name="nom"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="Dupont"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-stone-700 mb-1.5">Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="jean.dupont@email.com"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-stone-700 mb-1.5">Téléphone</label>
                    <input
                      type="tel"
                      name="telephone"
                      className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="+41 79 000 00 00"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-stone-700 mb-1.5">Adresse *</label>
                    <input
                      type="text"
                      name="adresse"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                      placeholder="Rue de Pramagnon 32"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1.5">NPA *</label>
                      <input
                        type="text"
                        name="npa"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="3979"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-stone-700 mb-1.5">Lieu *</label>
                      <input
                        type="text"
                        name="lieu"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                        placeholder="Grône"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-stone-700 mb-1.5">Type de service *</label>
                    <select
                      name="service"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                    >
                      <option value="">Sélectionnez un service</option>
                      <option value="Pose de carrelage">Pose de carrelage</option>
                      <option value="Rénovation salle de bain">Rénovation salle de bain</option>
                      <option value="Douche italienne">Douche italienne</option>
                      <option value="Faïence & mosaïque">Faïence &amp; mosaïque</option>
                      <option value="Carrelage extérieur">Carrelage extérieur</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                  <div className="mb-6">
                    <label className="block text-xs font-medium text-stone-700 mb-1.5">
                      Message <span className="text-stone-400">({charCount}/500)</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-2.5 text-sm bg-white border border-stone-200 rounded-lg focus:outline-none focus:border-amber-500 transition-colors resize-none"
                      placeholder="Décrivez votre projet (surface, type de carrelage, délai souhaité...)"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 bg-amber-700 text-white text-sm font-medium rounded-full hover:bg-amber-800 transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer ma demande
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>
                  {error ? (
                    <p className="text-xs text-red-600 text-center mt-3">
                      {error}
                    </p>
                  ) : (
                    <p className="text-xs text-stone-400 text-center mt-3">
                      On vous recontacte en général le jour même ou le lendemain.
                    </p>
                  )}
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
